import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Check, AlertTriangle, Clock } from "lucide-react";
import { usePasswordResetRequest } from "../hooks/usePasswordReset";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Progress } from "@/shared/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/shared/components/ui/alert";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";

// Interface para tipo de dados do formulário com campo opcional para captcha
interface RecoverPasswordFormData {
  email: string;
  captchaToken?: string;
}

const RecoverPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Estado para controlar se a solicitação foi enviada com sucesso
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  
  // Estados para proteção anti-abuso
  // Estados para mensagens de erro e rate limiting do backend
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  
  // Estados para controle de cooldown e proteções anti-abuso
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [cooldownProgress, setCooldownProgress] = useState(0);
  const [maxCooldown, setMaxCooldown] = useState(15);
  const [attemptsRemaining, setAttemptsRemaining] = useState(3);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  

  
  // Hook para solicitar recuperação de senha
  const passwordResetMutation = usePasswordResetRequest();
  
  // Esquema de validação usando Zod para o email
  const formSchema = z.object({
    email: z.string().email({ message: "Digite um email válido" }),
  });

  // Formulário React Hook Form com validação Zod
  const form = useForm<RecoverPasswordFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });
  
  // Verifica inicialmente se já existem tentativas anteriores
  useEffect(() => {
    checkPreviousAttempts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Função para verificar tentativas anteriores no localStorage
  const checkPreviousAttempts = () => {
    const attemptKey = 'password_reset_attempts';
    const storedAttempts = localStorage.getItem(attemptKey);
    
    if (storedAttempts) {
      const attempts = JSON.parse(storedAttempts);
      const currentTime = new Date().getTime();
      
      // Filtra tentativas válidas (menos de 1 hora)
      const validAttempts = attempts.filter((timestamp: number) => {
        return currentTime - timestamp < 60 * 60 * 1000; // 1 hora
      });
      
      // Atualiza o localStorage com apenas tentativas válidas
      localStorage.setItem(attemptKey, JSON.stringify(validAttempts));
      
      // Ativa proteções se necessário
      if (validAttempts.length >= 5) {
        // Iniciar com um cooldown maior para muitas tentativas
        startCooldown(30);
      }
      
      const remaining = Math.max(0, 3 - (validAttempts.length % 3));
      setAttemptsRemaining(remaining);
    }
  };
  
  // Iniciar contador de cooldown
  const startCooldown = (seconds: number = 15) => {
    setCooldownActive(true);
    setCooldownSeconds(seconds);
    setMaxCooldown(seconds);
    setCooldownProgress(0);
    
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }
    
    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds(seconds => {
        if (seconds <= 1) {
          clearInterval(cooldownTimerRef.current!);
          setCooldownActive(false);
          return 0;
        }
        return seconds - 1;
      });
      
      // Calcula a porcentagem de progresso inversa (0% no início, 100% no final)
      const newProgress = ((maxCooldown - (cooldownSeconds - 1)) / maxCooldown) * 100;
      setCooldownProgress(Math.min(newProgress, 100));
    }, 1000);
  };
  
  // Limpar timer ao desmontar componente
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);
  

  
  // Extrai minutos de uma mensagem de rate limiting do servidor
  const extractMinutesFromMessage = (message: string): number => {
    const match = message.match(/\d+/); // Encontra um ou mais dígitos
    if (match) {
      return parseInt(match[0]);
    }
    return 30; // Valor padrão de 30 minutos se não conseguir extrair
  };
  
  // Função para registrar uma tentativa local (complementar ao rate limiting do backend)
  const registerAttempt = () => {
    // Armazenar tentativas no localStorage para persistir entre recargas
    const currentTime = new Date().getTime();
    const attemptKey = 'password_reset_attempts';
    
    const storedAttempts = localStorage.getItem(attemptKey);
    let attempts = storedAttempts ? JSON.parse(storedAttempts) : [];
    
    // Filtra tentativas mais antigas que 1 hora
    attempts = attempts.filter((timestamp: number) => {
      return currentTime - timestamp < 60 * 60 * 1000; // 1 hora
    });
    
    // Adiciona nova tentativa
    attempts.push(currentTime);
    localStorage.setItem(attemptKey, JSON.stringify(attempts));
    
    // Ativa medidas de proteção com base no número de tentativas
    // Medidas locais complementares ao rate limiting do servidor
    if (!isRateLimited) {
      if (attempts.length >= 5) {
        startCooldown(30); // Cooldown maior após muitas tentativas
      } else if (attempts.length >= 3) {
        startCooldown(15);
      }
    }
    
    // Atualiza o número de tentativas restantes
    setAttemptsRemaining(Math.max(0, 3 - (attempts.length % 3)));
    
    return attempts.length;
  };

  const handleSubmit = async (data: RecoverPasswordFormData) => {
    // Resetar mensagens de erro anteriores
    setServerErrorMessage(null);
    
    try {
      
      // Registra a tentativa local e verifica necessidade de proteções
      if (!isRateLimited) {
        registerAttempt();
      }
      
      // Se não estiver em cooldown, permite a submissão
      if (!cooldownActive && !isRateLimited) {
        // Obter o idioma atual para enviar ao backend
        const locale = i18n.language ? (i18n.language === "pt" ? "pt-BR" : "en-US") : "pt-BR";
        
        await passwordResetMutation.mutateAsync({
          email: data.email,
          locale: locale
        });
        
        setSentEmail(data.email);
        setEmailSent(true);
      }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("Erro ao solicitar recuperação de senha:", error);
      
      // Verificar se é um erro de rate limit do backend
      if (error.response && error.response.status === 429) {
        const message = error.response.data.message;
        setServerErrorMessage(message);
        setIsRateLimited(true);
        
        // Extrair minutos da mensagem para iniciar cooldown
        if (message.includes("minutos") || message.includes("minutes")) {
          const minutes = extractMinutesFromMessage(message);
          startCooldown(minutes * 60); // Converter minutos para segundos
        } else {
          // Se não conseguir extrair minutos, assume 30 minutos de cooldown
          startCooldown(30 * 60);
        }
        return;
      }
      
      // Se for erro de servidor e não rate limit, registramos a tentativa
      registerAttempt();
      
      // Por segurança, ainda mostramos mensagem de sucesso em caso de erro
      // para evitar que um atacante descubra quais emails existem no sistema
      // (exceto no caso de rate limit que já tratamos acima)
      if (!error.response || error.response.status !== 429) {
        setSentEmail(data.email);
        setEmailSent(true);
      }
    }
  };

  // Se a solicitação foi enviada, exibe mensagem de sucesso
  if (emailSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
        <div className="w-full max-w-md space-y-4 animate-fade-in">
          <div className="flex justify-center mb-6">
            <img 
              src="/assets/logo.png" 
              alt="Prodgy Logo" 
              className="h-16 w-auto mb-2" 
            />
          </div>
          <Card className="w-full shadow-lg">
            <CardHeader className="space-y-3 pb-6 text-center">
              <div className="mx-auto mb-6 relative">
                <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="h-16 w-16 rounded-full bg-green-200 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-green-500 flex items-center justify-center animate-pulse">
                      <Check className="h-8 w-8 text-white" strokeWidth={3} />
                    </div>
                  </div>
                </div>
              </div>
              <CardTitle className="text-2xl font-medium tracking-tight text-center">
                {t("recoverPassword.emailSentTitle")}
              </CardTitle>
              <CardDescription className="text-center text-muted-foreground">
                {t("recoverPassword.emailSentDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-muted-foreground">
                {t("recoverPassword.sentTo")} <strong>{sentEmail}</strong>
              </p>
              <div className="mt-4 text-sm">
                <p>{t("recoverPassword.checkSpam")}</p>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 pt-2">
              <Button 
                variant="default" 
                className="w-full font-normal h-11" 
                onClick={() => navigate("/login")}
              >
                {t("recoverPassword.backToLogin")}
              </Button>
            </CardFooter>
          </Card>
        </div>
        
        {/* Powered by footer */}
        <div className="absolute bottom-4 right-4">
          <span className="text-sm text-muted-foreground opacity-80">
            {t("common.poweredBy")}{' '}
            <a 
              href="https://programmers.com.br" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-muted-foreground/80 transition-colors"
            >
              {t("common.companyName")}
            </a>
            {t("common.trademark")}
          </span>
        </div>
      </div>
    );
  }
  
  // Formulário para solicitar recuperação de senha
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-4 animate-fade-in">
        <div className="flex justify-center mb-6">
          <img 
            src="/assets/logo.png" 
            alt="Prodgy Logo" 
            className="h-16 w-auto mb-2" 
          />
        </div>
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-3 pb-8">
            <CardTitle className="text-2xl font-medium tracking-tight text-center">
              {t("recoverPassword.title")}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {t("recoverPassword.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input 
                          {...field} 
                          type="email" 
                          placeholder={t("recoverPassword.emailLabel")}  
                          disabled={passwordResetMutation.isPending || cooldownActive}
                          className="h-11"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                

                
                {/* Alerta para rate limit do backend */}
                {isRateLimited && serverErrorMessage && (
                  <Alert variant="destructive" className="bg-red-50 text-red-800 border-red-300">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{t("recoverPassword.tooManyAttempts")}</AlertTitle>
                    <AlertDescription>
                      <div className="space-y-2">
                        <p>{serverErrorMessage}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4" />
                          <span>{t("recoverPassword.cooldownMessage", { seconds: cooldownSeconds })}</span>
                        </div>
                        <Progress value={cooldownProgress} className="h-2" />
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Alerta para cooldown local (complementar) */}
                {cooldownActive && !isRateLimited && (
                  <Alert variant="default" className="bg-amber-50 text-amber-800 border-amber-200">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>{t("recoverPassword.rateLimit")}</AlertTitle>
                    <AlertDescription>
                      <div className="space-y-2">
                        <p>{t("recoverPassword.rateLimitDescription")}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4" />
                          <span>{t("recoverPassword.cooldownMessage", { seconds: cooldownSeconds })}</span>
                        </div>
                        <Progress value={cooldownProgress} className="h-2" />
                        {attemptsRemaining > 0 && (
                          <p className="text-sm mt-1">
                            {t("recoverPassword.attemptsRemaining", { attempts: attemptsRemaining })}
                          </p>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full font-normal h-11 relative" 
                  disabled={passwordResetMutation.isPending || cooldownActive || isRateLimited}
                >
                  {passwordResetMutation.isPending ? (
                    <div className="flex items-center justify-center">
                      <LoadingSpinner className="h-5 w-5 text-white" size="sm" />
                    </div>
                  ) : isRateLimited ? (
                    <div className="flex items-center justify-center gap-2">
                      <Clock className="h-5 w-5" />
                      <span>{t("recoverPassword.cooldownMessage", { seconds: cooldownSeconds })}</span>
                    </div>
                  ) : cooldownActive ? (
                    <div className="flex items-center justify-center gap-2">
                      <Clock className="h-5 w-5" />
                      <span>{t("recoverPassword.cooldownMessage", { seconds: cooldownSeconds })}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Mail className="h-5 w-5" />
                      <span>{t("recoverPassword.submit")}</span>
                    </div>
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex justify-center pt-4">
            <Button 
              variant="link" 
              onClick={() => navigate("/login")} 
              className="text-muted-foreground hover:text-muted-foreground/80 transition-colors underline underline-offset-4"
            >
              {t("recoverPassword.backToLogin")}
            </Button>
          </CardFooter>
        </Card>
      </div>
      
      {/* Powered by footer */}
      <div className="absolute bottom-4 right-4">
        <span className="text-sm text-muted-foreground opacity-80">
          {t("auth.login.poweredBy")} <a 
            href="https://programmers.com.br" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-muted-foreground/80 transition-colors"
          >
            Programmers
          </a>®
        </span>
      </div>
    </div>
  );
};

export default RecoverPassword;
